import { DeviceLabels } from 'amazon-chime-sdk-component-library-react';
import { AUDIO_CONSTRAINTS } from './audioConstraints';
import { isVirtualAudioDevice } from './virtualAudioPatterns';

// macOS Continuity routes an iPhone/iPad as an enumerated audio_input device.
// The label format is "{device-name} {Microphone-localized}" — e.g.
// "iPhone Microphone", "iPhone (7) Microphone", "Julius iPhone 2022 Microphone",
// or "iPhone Mikrofon" (German), "iPad Microfono" (Italian). The post-prefix
// gate ([\s\d]) avoids matching arbitrary USB devices branded "iPhoneMicPro".
// Verification gap: if a Japanese-locale Continuity device uses an ideographic
// space (U+3000), this regex misses it — widen to [\s\d　] if confirmed.
const CONTINUITY_MIC_LABEL = /^(iPhone|iPad)[\s\d]/i;

export function isContinuityMicLabel(label: string | null | undefined): boolean {
  if (!label) return false;
  return CONTINUITY_MIC_LABEL.test(label);
}

// iOS Safari labels: "Front Camera" / "Back Camera" / "Back Triple Camera" /
// "Back Ultra Wide Camera" / "Back Dual Wide Camera". Android Chrome labels
// are inconsistent across OEMs/versions but commonly include "Front" / "Back"
// or "facing front" / "facing back". Substring match (case-insensitive) is
// adequate for these conventions; uncommon enough on the desktop side that
// the mobile gate makes the risk acceptable.
//
// Localization caveat: iOS Safari localizes some MediaDeviceInfo labels
// (see iPhone Mikrofon / iPad Microfono cases above). Camera-label
// localization unverified at merge time —
// TODO(or, 2026-05-04): test on a non-English-locale iPhone and either
// confirm English-only labels OR extend the regex with localized variants.
// Until then, this helper assumes English labels. Non-matching labels fall
// through to the existing alphabetical default (no regression vs. today).
//
// Empty-label browsers (Firefox iOS, DuckDuckGo iOS) get the fallback
// string "Camera" from deduplicateDevices, which won't match — those users
// keep the alphabetical-first default. Consistent with the existing
// empty-deviceId limitations on those engines.
//
// The mobile UA gate (DeviceManager.isMobileUA) is the load-bearing guard
// against false positives, not the regex itself: a hypothetical desktop
// webcam with "front" in its product name would be reordered if this ever
// ran on desktop. Don't loosen the gate without revisiting this assumption.
export function isFrontFacingCameraLabel(label: string | null | undefined): boolean {
  if (!label) return false;
  return /front/i.test(label);
}

export class DeviceManager {
  private audioInputDevices: MediaDeviceInfo[] = [];
  private videoInputDevices: MediaDeviceInfo[] = [];
  private audioOutputDevices: MediaDeviceInfo[] = [];
  private selectedAudioInputDevice: string | null = null;
  private selectedVideoInputDevice: string | null = null;
  private selectedAudioOutputDevice: string | null = null;

  async listAndSelectDevices(deviceLabels: DeviceLabels): Promise<void> {
    try {
      // Request permissions if needed - but don't fail if permissions are denied
      try {
        if (deviceLabels === DeviceLabels.AudioAndVideo || deviceLabels === DeviceLabels.Audio) {
          let audioStream: MediaStream;
          try {
            audioStream = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS });
          } catch {
            // Fall back to bare audio: true for iOS browsers that don't
            // support all constraints (e.g. DuckDuckGo)
            audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          }
          audioStream.getTracks().forEach(track => track.stop());
        }
        if (deviceLabels === DeviceLabels.AudioAndVideo || deviceLabels === DeviceLabels.Video) {
          const videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
          videoStream.getTracks().forEach(track => track.stop());
        }
      } catch (permissionError) {
        console.warn('Device permissions denied or no devices available:', permissionError);
        // Continue without devices rather than throwing an error
      }

      // Enumerate all devices
      const devices = await navigator.mediaDevices.enumerateDevices();

      // Filter devices by type and deduplicate
      const rawAudioInputs = devices.filter(device => device.kind === 'audioinput');
      const rawVideoInputs = devices.filter(device => device.kind === 'videoinput');
      const rawAudioOutputs = devices.filter(device => device.kind === 'audiooutput');

      this.audioInputDevices = this.deduplicateDevices(rawAudioInputs);
      this.videoInputDevices = this.sortVideoInputsByFacing(this.deduplicateDevices(rawVideoInputs));
      this.audioOutputDevices = this.deduplicateDevices(rawAudioOutputs);

      // If the previously selected device is no longer in the enumerated
      // list (e.g. AirPods were unplugged), clear the selection so the
      // auto-select guards below pick a fallback. Without this, the stale
      // id propagates into React state and leaves the SDK pointing at a
      // device that no longer exists.
      if (
        this.selectedAudioInputDevice &&
        !this.audioInputDevices.some(d => d.deviceId === this.selectedAudioInputDevice)
      ) {
        this.selectedAudioInputDevice = null;
      }
      if (
        this.selectedVideoInputDevice &&
        !this.videoInputDevices.some(d => d.deviceId === this.selectedVideoInputDevice)
      ) {
        this.selectedVideoInputDevice = null;
      }
      if (
        this.selectedAudioOutputDevice &&
        !this.audioOutputDevices.some(d => d.deviceId === this.selectedAudioOutputDevice)
      ) {
        this.selectedAudioOutputDevice = null;
      }

      // Only select default devices if no device is currently selected.
      // This preserves user selections when refreshing the device list.
      // De-prioritize virtual audio drivers (BlackHole, eqMac, VB-Audio,
      // etc.) — picking one as the lobby default silently routes audio
      // through a routing app instead of the user's mic, prompting an
      // immediate post-meeting-start re-pick. Fall back to [0] only if
      // every enumerated device is virtual.
      if (deviceLabels === DeviceLabels.AudioAndVideo || deviceLabels === DeviceLabels.Audio) {
        if (this.audioInputDevices.length > 0 && !this.selectedAudioInputDevice) {
          this.selectedAudioInputDevice = this.pickPreferredInput(this.audioInputDevices).deviceId;
        }
      }
      if (deviceLabels === DeviceLabels.AudioAndVideo || deviceLabels === DeviceLabels.Video) {
        if (this.videoInputDevices.length > 0 && !this.selectedVideoInputDevice) {
          this.selectedVideoInputDevice = this.pickPreferredInput(this.videoInputDevices).deviceId;
        }
      }

      // Auto-select audio output: filter virtual drivers as outer tier,
      // then run preferNonDisplayPort on the resulting pool. Composes
      // cleanly with the existing DP-vs-non-DP and good-label preference.
      if (this.audioOutputDevices.length > 0 && !this.selectedAudioOutputDevice) {
        this.selectedAudioOutputDevice = this.pickPreferredOutput(this.audioOutputDevices).deviceId;
      }
    } catch (error) {
      console.error('Error listing devices:', error);
      // Don't throw error - allow joining without devices
    }
  }

  getAudioInputDevices(): MediaDeviceInfo[] {
    return this.audioInputDevices;
  }

  getVideoInputDevices(): MediaDeviceInfo[] {
    return this.videoInputDevices;
  }

  getAudioOutputDevices(): MediaDeviceInfo[] {
    return this.audioOutputDevices;
  }

  getSelectedAudioInputDevice(): string | null {
    return this.selectedAudioInputDevice;
  }

  getSelectedVideoInputDevice(): string | null {
    return this.selectedVideoInputDevice;
  }

  getSelectedAudioOutputDevice(): string | null {
    return this.selectedAudioOutputDevice;
  }

  // Validate device exists before selection; fall back to first available
  async selectAudioInputDevice(deviceId: string): Promise<void> {
    if (this.audioInputDevices.some(d => d.deviceId === deviceId)) {
      this.selectedAudioInputDevice = deviceId;
    } else if (this.audioInputDevices.length > 0) {
      console.warn(`Audio input device ${deviceId} not found, falling back`);
      this.selectedAudioInputDevice = this.audioInputDevices[0].deviceId;
    }
  }

  async selectVideoInputDevice(deviceId: string): Promise<void> {
    if (this.videoInputDevices.some(d => d.deviceId === deviceId)) {
      this.selectedVideoInputDevice = deviceId;
    } else if (this.videoInputDevices.length > 0) {
      console.warn(`Video input device ${deviceId} not found, falling back`);
      this.selectedVideoInputDevice = this.videoInputDevices[0].deviceId;
    }
  }

  async selectAudioOutputDevice(deviceId: string): Promise<void> {
    if (this.audioOutputDevices.some(d => d.deviceId === deviceId)) {
      this.selectedAudioOutputDevice = deviceId;
    } else if (this.audioOutputDevices.length > 0) {
      console.warn(`Audio output device ${deviceId} not found, falling back`);
      this.selectedAudioOutputDevice = this.audioOutputDevices[0].deviceId;
    }
  }

  /**
   * Check if a device is a DisplayPort device (case-insensitive)
   */
  private isDisplayPortDevice(device: MediaDeviceInfo): boolean {
    return Boolean(device.label && device.label.toLowerCase().includes('displayport'));
  }

  /**
   * Prefer a non-DisplayPort device for default selection,
   * but fall back to any device if all are DisplayPort.
   */
  private preferNonDisplayPort(devices: MediaDeviceInfo[]): MediaDeviceInfo {
    const nonDP = devices.filter(d => !this.isDisplayPortDevice(d));
    const candidates = nonDP.length > 0 ? nonDP : devices;

    return candidates.find(d =>
      d.label &&
      d.label.trim() !== '' &&
      !d.label.includes('Unknown') &&
      !d.label.includes('Default')
    ) || candidates[0];
  }

  /**
   * Auto-pick for audio_input / video_input: prefer the first non-virtual
   * device. Falls back to [0] only if every device is virtual (theoretical
   * edge case — user has only virtual audio routers, intentionally).
   *
   * Mirrors the Android-phantom-filter precedent (deduplicateDevices already
   * filters earpiece/speakerphone/receiver before they reach this function)
   * but virtual drivers stay in the visible device list — only auto-pick
   * deprioritizes them. A user can still pick BlackHole explicitly via
   * MicSelection if they want it.
   */
  private pickPreferredInput(devices: MediaDeviceInfo[]): MediaDeviceInfo {
    const nonVirtual = devices.find(d => !isVirtualAudioDevice(d));
    return nonVirtual ?? devices[0];
  }

  // Note: deduplicateDevices's phantom-filter (line 307–317) uses a narrower
  // /iPhone|iPad|iPod/i and inherits the iPadOS-as-macOS gap. Pre-existing —
  // fix in a separate PR alongside any phantom-filter regression report.
  private isMobileUA(): boolean {
    const ua = navigator.userAgent;
    if (/iPhone|iPod|Android/i.test(ua)) return true;
    // iPad: explicit token (older iOS, or desktop-mode-toggled), OR
    // iPadOS-as-macOS detection via touch-points (iPadOS 13+ default).
    if (/iPad/i.test(ua)) return true;
    if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return true;
    return false;
  }

  // Stable sort: front-facing cameras first, otherwise preserve the order
  // returned by aggressiveDeduplication (alphabetical). Desktop UA → no-op
  // so non-mobile users aren't affected. Called from listAndSelectDevices
  // and forceRefreshDevices on the videoInputDevices list only.
  private sortVideoInputsByFacing(devices: MediaDeviceInfo[]): MediaDeviceInfo[] {
    if (!this.isMobileUA()) return devices;
    return [...devices].sort((a, b) => {
      const aFront = isFrontFacingCameraLabel(a.label);
      const bFront = isFrontFacingCameraLabel(b.label);
      if (aFront && !bFront) return -1;
      if (!aFront && bFront) return 1;
      return 0;
    });
  }

  /**
   * Auto-pick for audio_output: filter virtual drivers as outer tier,
   * then run preferNonDisplayPort on the resulting pool. Composes with
   * existing DP-vs-non-DP and good-label preference. If all devices are
   * virtual, the inner pool is the original list and preferNonDisplayPort
   * runs as today (no regression for users with only virtual outputs).
   */
  private pickPreferredOutput(devices: MediaDeviceInfo[]): MediaDeviceInfo {
    const nonVirtual = devices.filter(d => !isVirtualAudioDevice(d));
    const pool = nonVirtual.length > 0 ? nonVirtual : devices;
    return this.preferNonDisplayPort(pool);
  }

  /**
   * Force refresh devices - useful when AirPods are connected after initial load
   */
  async forceRefreshDevices(): Promise<void> {
    try {
      // Try to get a media stream to trigger device refresh
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      stream.getTracks().forEach(track => track.stop());

      // Wait a moment for devices to be released
      await new Promise(resolve => setTimeout(resolve, 300));

      // Re-enumerate all devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const rawAudioInputs = devices.filter(device => device.kind === 'audioinput');
      const rawVideoInputs = devices.filter(device => device.kind === 'videoinput');
      const rawAudioOutputs = devices.filter(device => device.kind === 'audiooutput');

      this.audioInputDevices = this.deduplicateDevices(rawAudioInputs);
      this.videoInputDevices = this.sortVideoInputsByFacing(this.deduplicateDevices(rawVideoInputs));
      this.audioOutputDevices = this.deduplicateDevices(rawAudioOutputs);

      // Clear stale selected ids before auto-selecting fallbacks
      if (
        this.selectedAudioInputDevice &&
        !this.audioInputDevices.some(d => d.deviceId === this.selectedAudioInputDevice)
      ) {
        this.selectedAudioInputDevice = null;
      }
      if (
        this.selectedVideoInputDevice &&
        !this.videoInputDevices.some(d => d.deviceId === this.selectedVideoInputDevice)
      ) {
        this.selectedVideoInputDevice = null;
      }
      if (
        this.selectedAudioOutputDevice &&
        !this.audioOutputDevices.some(d => d.deviceId === this.selectedAudioOutputDevice)
      ) {
        this.selectedAudioOutputDevice = null;
      }

      // Auto-select if none selected. Same virtual-driver de-prioritization
      // as listAndSelectDevices — see pickPreferredInput / pickPreferredOutput.
      if (this.audioInputDevices.length > 0 && !this.selectedAudioInputDevice) {
        this.selectedAudioInputDevice = this.pickPreferredInput(this.audioInputDevices).deviceId;
      }
      if (this.videoInputDevices.length > 0 && !this.selectedVideoInputDevice) {
        this.selectedVideoInputDevice = this.pickPreferredInput(this.videoInputDevices).deviceId;
      }
      if (this.audioOutputDevices.length > 0 && !this.selectedAudioOutputDevice) {
        this.selectedAudioOutputDevice = this.pickPreferredOutput(this.audioOutputDevices).deviceId;
      }
    } catch (error) {
      console.warn('Force refresh failed:', error);
    }
  }

  /**
   * Deduplicate devices by removing entries that are essentially the same device
   * but with different labels (e.g., "AirPods" vs "Default - AirPods")
   */
  private deduplicateDevices(devices: MediaDeviceInfo[]): MediaDeviceInfo[] {
    // Drop empty-label audio/video inputs. An empty label on an input
    // means the user hasn't granted device-permission labels yet; the
    // device is unusable until then, and including it in the dropdown
    // surfaces a confusing "Microphone" entry that maps to nothing
    // selectable. Audio outputs are exempt — AirPods (and other
    // Bluetooth output devices) regularly enumerate with empty labels
    // even with audio permission granted, so we keep them visible.
    devices = devices.filter((device) => {
      if (device.kind === 'audiooutput') return true;
      return Boolean(device.label && device.label.trim() !== '');
    });

    // Filter phantom audio HAL devices that appear regardless of hardware.
    // These devices silently break audio when selected (startAudioInputDevice
    // resolves but audio stops working, and the dropdown becomes disabled).
    // Android exposes "Earpiece", "Speakerphone" (input), and "Receiver" (output).
    // iOS Safari exposes "Receiver" (output).
    // Labels vary by OEM — use case-insensitive substring matching.
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isAndroid || isIOS) {
      devices = devices.filter((device) => {
        const label = device.label?.trim().toLowerCase() ?? '';
        if (device.kind === 'audioinput' && label.includes('earpiece')) return false;
        if (device.kind === 'audioinput' && label.includes('speakerphone')) return false;
        if (device.kind === 'audiooutput' && label.includes('receiver')) return false;
        return true;
      });
    }

    const deviceMap = new Map<string, MediaDeviceInfo>();

    const kindLabels: Record<string, string> = {
      audioinput: 'Microphone',
      videoinput: 'Camera',
      audiooutput: 'Speaker',
    };

    for (const device of devices) {
      const deviceLabel = device.label?.trim()
        ? device.label
        : kindLabels[device.kind] ?? 'Unknown Device';

      // Normalize the label by removing "Default - " prefix and other common prefixes
      const normalizedLabel = deviceLabel
        .replace(/^Default - /, '')
        .replace(/^Built-in /, '')
        .replace(/^Internal /, '')
        .trim();

      // Create a key based on the normalized label (not device ID, since same device can have different IDs)
      const key = `${device.kind}-${normalizedLabel}`;

      // Check if we already have this device
      const existingDevice = deviceMap.get(key);

      if (!existingDevice) {
        deviceMap.set(key, device);
      } else {
        // We already have this device, prefer the non-default version
        const currentIsDefault = deviceLabel.startsWith('Default - ');
        const existingIsDefault = existingDevice.label.startsWith('Default - ');

        if (!currentIsDefault && existingIsDefault) {
          deviceMap.set(key, device);
        }
      }
    }

    // If we still have duplicates after the first pass, try a more aggressive approach
    return this.aggressiveDeduplication(Array.from(deviceMap.values()));
  }

  /**
   * More aggressive deduplication for cases where devices still appear duplicated
   */
  private aggressiveDeduplication(devices: MediaDeviceInfo[]): MediaDeviceInfo[] {
    const kindLabels: Record<string, string> = {
      audioinput: 'Microphone',
      videoinput: 'Camera',
      audiooutput: 'Speaker',
    };
    const finalDevices: MediaDeviceInfo[] = [];
    const seenLabels = new Set<string>();

    for (const device of devices) {
      // Create a very normalized version of the label for comparison
      const deviceLabel = device.label?.trim()
        ? device.label
        : kindLabels[device.kind] ?? 'Unknown Device';

      const veryNormalizedLabel = deviceLabel
        .toLowerCase()
        .replace(/^default - /, '')
        .replace(/^built-in /, '')
        .replace(/^internal /, '')
        .replace(/\(.*\)/g, '') // Remove anything in parentheses
        .replace(/\s+/g, ' ') // Normalize whitespace
        .trim();

      if (!seenLabels.has(veryNormalizedLabel)) {
        seenLabels.add(veryNormalizedLabel);
        finalDevices.push(device);
      }
    }

    // Stable two-key sort: (isVirtual asc, isDefaultPrefix asc, label asc).
    // Tiers, lowest rank first:
    //   1. Non-Default, non-virtual physical devices (alphabetical)
    //   2. Default - non-virtual physical devices (alphabetical)
    //   3. Non-Default virtual devices (alphabetical)
    //   4. Default - virtual devices (alphabetical)
    //
    // Audio_input / audio_output controls (MicSelection, SpeakerSelection) render
    // this sorted list; ranking virtual drivers behind physical ones ensures
    // the auto-pick (which takes [0]) lands on a real device when one exists.
    // Within-tier `Default - X` after `X` is preserved by the unchanged
    // alphabetical fallback within each tier — see the corresponding test.
    return finalDevices.sort((a, b) => {
      const aIsVirtual = isVirtualAudioDevice(a);
      const bIsVirtual = isVirtualAudioDevice(b);
      if (aIsVirtual && !bIsVirtual) return 1;
      if (!aIsVirtual && bIsVirtual) return -1;

      const aIsDefault = a.label.startsWith('Default - ');
      const bIsDefault = b.label.startsWith('Default - ');
      if (aIsDefault && !bIsDefault) return 1;
      if (!aIsDefault && bIsDefault) return -1;

      return a.label.localeCompare(b.label);
    });
  }
}
